import {useCallback, useEffect, useRef} from 'react';

import {useDispatch, useSelector} from 'react-redux';
import {noop} from 'lodash';

import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {IMoneycardsRes} from '~/shared/services/bffService/api/interfaces/responses';
import {selectUserData} from '~/shared/store/selectors';
import {StateAsyncProperty} from '~/shared/store/redux-toolbelt-10bis';
import {
  getMoneyCardsIfNeeded,
  selectHasWalletCard,
  selectMoneyCards,
  selectMoneyCardsError,
  selectMoneyCardsLoaded,
  selectMoneyCardsLoading,
  selectTenbisCreditBalance,
} from '~/shared/store/storeModules/moneyCards';

interface UseMoneyCardsReturn extends StateAsyncProperty<IMoneycardsRes> {
  refetch: () => Promise<void>;
  tenbisCreditBalance: number;
  hasWalletCard: boolean;
}

// Binds the consumers to the money cards store module, which fetches the cards once per visit
const useMoneyCards = (): UseMoneyCardsReturn => {
  const dispatch = useDispatch();

  const userData = useSelector(selectUserData);
  const data = useSelector(selectMoneyCards);
  const loading = useSelector(selectMoneyCardsLoading);
  const loaded = useSelector(selectMoneyCardsLoaded);
  const error = useSelector(selectMoneyCardsError);
  const tenbisCreditBalance = useSelector(selectTenbisCreditBalance);
  const hasWalletCard = useSelector(selectHasWalletCard);

  const fetchMoneyCards = useCallback(async (force?: boolean) => {
    try {
      await dispatch(getMoneyCardsIfNeeded({force}));
    } catch (fetchError) {
      // Failures are kept on the store and rendered by the consumers, apart from a 401, which the
      // store leaves to the auth service to refresh and retry, and which ends in a logout when it fails
      if (is401Error(fetchError)) {
        await handleRefreshToken(fetchError, () => dispatch(getMoneyCardsIfNeeded({force: true}))).catch(noop);
      }
    }
  }, [dispatch]);

  const refetch = useCallback(() => fetchMoneyCards(true), [fetchMoneyCards]);

  // The cards are fetched once a user is available and the store holds neither a request in flight
  // nor a result, so every consumer after the first one is served by the store. Each consumer asks
  // for them at most once, a failed request being retried through `refetch` rather than here
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    if (userData && !loading && !loaded && !hasRequestedRef.current) {
      hasRequestedRef.current = true;
      fetchMoneyCards();
    }
  }, [userData, loading, loaded, fetchMoneyCards]);

  return {
    data,
    loading,
    loaded,
    error,
    refetch,
    tenbisCreditBalance,
    hasWalletCard,
  };
};

export default useMoneyCards;
